import * as React from 'react'
import styled from 'styled-components'
import { fontSize, theme } from 'utils/styledHelper'

import Checkbox from './Checkbox'
import injectE2E from './injectE2E'
import Radio from './Radio'

export type HtmlInputType =
  | 'checkbox'
  | 'date'
  | 'number'
  | 'password'
  | 'radio'
  | 'search'
  | 'text'

export type PropsType = {
  className?: string
  disabled?: boolean
  e2e?: string
  error?: string
  input?: {}
  label?: string
  name?: string
  onChange?: (e: any) => void
  onFocus?: () => void
  placeholder?: string
  // FIXME For some reason I couldn't make React.RefObject<HTMLInputElement> work
  ref: any
  renderSuffix?: (disabled?: boolean) => React.ReactNode
  type?: HtmlInputType
  value?: string | number
  width?: string
}

const Container = styled.div<{ width?: string }>`
  display: flex;
  flex-direction: column;
  width: ${(p) => p.width};
  position: relative;
`

const InputWrapper = styled.input.attrs(injectE2E)<{ error: boolean; width?: string }>`
  display: block;
  height: 2.7rem;
  width: ${(p) => p.width};
  padding: 0 1rem;
  box-sizing: border-box;
  font-family: ${theme('fontPrimary')};
  font-size: ${fontSize('md')};
  color: ${theme('inputColor')};
  border-width: 1px;
  border-color: ${(props) =>
    props.error != null ? theme('dangerColor')(props) : theme('inputBorderColor')(props)};
  border-style: solid;
  border-radius: ${theme('inputBorderRadius')};
  background-color: ${theme('inputBackgroundColor')};
  background-image: none;
  outline-width: 0;
  user-select: text;
  transition: box-shadow 0.25s ease-out;
  &:disabled {
    color: ${theme('inputColor')};
    border-color: ${theme('inputBorderColor')};
    background: ${theme('inputBackgroundColorDisabled')};
    cursor: not-allowed;
  }
  &:active:not(:disabled),
  &:focus:not(:disabled) {
    box-shadow: 0 0 0 1px
      ${(props) => (props.error != null ? 'transparent' : theme('inputActiveColor'))};
  }
  &::placeholder {
    color: ${theme('inputPlaceholderColor')};
    font-size: ${fontSize('sm')};
  }
`

export const InputLabel = styled.label<{ disabled?: boolean; error: boolean }>`
  font-family: ${theme('fontPrimary')};
  font-size: ${fontSize('sm')};
  padding-bottom: 0.5rem;
  color: ${(props) =>
    props.error === true && props.disabled !== true
      ? props.theme.dangerColor
      : props.theme.inputLabelColor};
  ${(props) => (props.disabled === true ? 'cursor: not-allowed' : '')};
`

export const Input = ({ className = '', input, ref, renderSuffix, type, ...rest }: PropsType) => {
  const hasLabel = rest.label != null
  const hasError = rest.error != null

  if (type === 'checkbox') {
    return <Checkbox className={className} input={input} type='checkbox' {...rest} />
  }

  if (type === 'radio') {
    return <Radio className={className} input={input} type='radio' {...rest} />
  }

  if (hasLabel || hasError) {
    return (
      <Container className={className} width={rest.width}>
        <InputLabel disabled={rest.disabled} error={hasError} htmlFor={rest.name}>
          {`${rest.label || ''}${hasLabel && hasError ? ' - ' : ''}${rest.error || ''}`}
        </InputLabel>
        <InputWrapper {...input} {...rest} ref={ref} type={type} />
        {renderSuffix && renderSuffix(rest.disabled)}
      </Container>
    )
  }

  return (
    <Container className={className} width={rest.width}>
      <InputWrapper {...input} {...rest} ref={ref} type={type} />
      {renderSuffix && renderSuffix(rest.disabled)}
    </Container>
  )
}

Input.defaultProps = {
  disabled: false,
  e2e: 'defaultInput',
  type: 'text',
  width: '100%'
}

export default React.forwardRef<HTMLInputElement, PropsType>((props, ref) => (
  <Input {...props} ref={ref} />
))
